import type { AnalysisEnvelope, TradingCluster } from "../api/types";
import type { TapeModel, TapeRunGroup } from "./types";

function toTime(iso: string): number {
  const value = Date.parse(iso);
  return Number.isNaN(value) ? 0 : value;
}

export function buildTapeModel({
  currentAnalysis,
  history,
  clusters,
}: {
  currentAnalysis: AnalysisEnvelope | null;
  history: AnalysisEnvelope[];
  clusters: TradingCluster[];
}): TapeModel {
  const snapshots = [currentAnalysis, ...history].filter(
    (item): item is AnalysisEnvelope => Boolean(item),
  );
  const sortedSnapshots = [...snapshots].sort(
    (a, b) => toTime(b.generated_at) - toTime(a.generated_at) || a.id.localeCompare(b.id),
  );
  const sortedClusters = [...clusters].sort(
    (a, b) =>
      toTime(b.first_seen_at) - toTime(a.first_seen_at) ||
      a.cluster_id.localeCompare(b.cluster_id),
  );
  const consumedByAnyRun = new Set(
    sortedSnapshots.flatMap((snapshot) => snapshot.body?.input_cluster_ids ?? []),
  );
  const freshCutoff = currentAnalysis ? toTime(currentAnalysis.generated_at) : 0;
  const freshClusters = sortedClusters.filter(
    (cluster) =>
      !consumedByAnyRun.has(cluster.cluster_id) &&
      toTime(cluster.first_seen_at) > freshCutoff,
  );
  const tapeClusters = new Set(freshClusters.map((cluster) => cluster.cluster_id));

  const groups: TapeRunGroup[] = sortedSnapshots.map((snapshot) => {
    const inputIds = new Set(snapshot.body?.input_cluster_ids ?? []);
    const driverWeights = new Map(
      (snapshot.body?.key_drivers ?? []).map((driver) => [
        driver.supporting_cluster_id,
        driver.weight,
      ]),
    );

    const groupClusters = sortedClusters
      .filter((cluster) => {
        if (tapeClusters.has(cluster.cluster_id)) return false;
        return inputIds.has(cluster.cluster_id);
      })
      .map((cluster) => ({
        cluster,
        consumed: inputIds.has(cluster.cluster_id),
        driverWeight: driverWeights.get(cluster.cluster_id),
      }));

    for (const item of groupClusters) tapeClusters.add(item.cluster.cluster_id);
    return { snapshot, clusters: groupClusters };
  });

  const unplacedClusters = sortedClusters.filter(
    (cluster) => !tapeClusters.has(cluster.cluster_id),
  );
  return { freshClusters, groups, unplacedClusters };
}
